import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClient, QueryClientProvider, hashKey } from '@tanstack/react-query'
import {
  Outlet,
  RouterProvider,
  createRootRouteWithContext,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'

import { AppLayout } from '@/components/layout/AppLayout'
import { LoginPage } from '@/features/auth/LoginPage'
import { ForceChangePasswordPage } from '@/features/auth/ForceChangePasswordPage'
import { ForgotPasswordPage, ResetPasswordPage } from '@/features/auth/PasswordResetPages'
import { TotpSetupPage } from '@/features/auth/TotpSetupPage'
import { DashboardPage } from '@/features/dashboard/DashboardPage'
import { CasesPage } from '@/features/cases/CasesPage'
import { TeleQueuePage } from '@/features/telecalling/TeleQueuePage'
import { CallConsolePage } from '@/features/telecalling/CallConsolePage'
import { CaseDetailPage } from '@/features/cases/CaseDetailPage'
import { IngestionPage } from '@/features/ingestion/IngestionPage'
import { BatchDetailPage } from '@/features/ingestion/BatchDetailPage'
import { AllocationsPage } from '@/features/allocations/AllocationsPage'
import { ReconciliationPage } from '@/features/reconciliation/ReconciliationPage'
import { ApprovalCenterPage } from '@/features/trips/ApprovalCenterPage'
import { PlannerPage } from '@/features/trips/PlannerPage'
import { LiveTripsPage } from '@/features/trips/LiveTripsPage'
import { TripDetailPage } from '@/features/trips/TripDetailPage'
import { TripReviewPage } from '@/features/trips/TripReviewPage'
import { SettingsPage } from '@/features/settings/SettingsPage'
import { UsersAdminPage } from '@/features/admin/UsersAdminPage'
import { OrgAdminPage } from '@/features/admin/OrgAdminPage'
import { RolesAdminPage } from '@/features/admin/RolesAdminPage'
import { NotificationsAdminPage } from '@/features/admin/NotificationsAdminPage'
import { CollectionsPage } from '@/features/collections/CollectionsPage'
import { AttendanceMisPage, PtpMisPage, VisitMisPage } from '@/features/reports/ReportPages'
import { VisitPhotoPage } from '@/features/reports/VisitPhotoPage'
import { SettlementsPage } from '@/features/settlements/SettlementsPage'
import { AuditPage } from '@/features/admin/AuditPage'
import { PiiAccessPage } from '@/features/admin/PiiAccessPage'
import { CashConfirmPage } from '@/features/cash-confirm/CashConfirmPage'
import { ErasurePage } from '@/features/erasure/ErasurePage'
import { ProfilePage } from '@/features/profile/ProfilePage'
import { PlatformDashboardPage } from '@/features/platform/PlatformDashboardPage'
import { TenantsPage } from '@/features/platform/TenantsPage'
import { SecurityPage } from '@/features/profile/SecurityPage'
import { useAuthStore, hasLiveSession, isTotpPending } from '@/lib/auth/store'

import './index.css'

/**
 * Every cache entry is scoped to the tenant that fetched it.
 *
 * <p>Signing out of one tenant and into another in the same tab left every
 * cached entry in place — no query key carried the tenant, and nothing
 * cleared the cache on login or logout. React Query then served the previous
 * tenant's data to the new session until each query went stale: the sidebar
 * showed the old slug (`useMe` has a 60s staleTime), and case lists and
 * customer names would render the same way. A wrong label is cosmetic; the
 * customer data behind it is not.</p>
 *
 * <p>Done as a hash function rather than by prefixing ~30 call sites because
 * this cannot be forgotten. A new `useQuery` added later is scoped whether or
 * not its author knows this exists, and the failure mode of the alternative —
 * one key missed — is a silent cross-tenant read.</p>
 *
 * <p>The store is a module-level singleton, so reading it here is synchronous
 * and consistent. Filters like `invalidateQueries({ queryKey })` still match
 * structurally on the key itself and are unaffected.</p>
 */
function tenantScopedKeyHash(queryKey: readonly unknown[]): string {
  return hashKey([useAuthStore.getState().tenantSlug ?? 'anon', ...queryKey])
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: (failureCount, error: unknown) => {
        const status = (error as { response?: { status?: number } })?.response?.status
        if (status && status >= 400 && status < 500) return false
        return failureCount < 2
      },
      staleTime: 30_000,
      refetchOnWindowFocus: false,
      queryKeyHashFn: tenantScopedKeyHash,
    },
  },
})

interface RouterContext { queryClient: QueryClient }

const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => <Outlet />,
})

const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  component: LoginPage,
  beforeLoad: () => {
    const state = useAuthStore.getState()
    // Only skip the login form for a LIVE session (access token valid, or a
    // refresh token that can revive it). A fully-dead token pair must not
    // bounce to '/' where the authed guard would just expire it again.
    if (!hasLiveSession(state)) return
    // totpPending: credentials were already proven, just not fully signed
    // in yet — send back to enrolment rather than the dashboard.
    if (isTotpPending(state)) throw redirect({ to: '/auth/totp-setup' })
    throw redirect({ to: '/' })
  },
})

/** Public: request a reset link, and the page the emailed link opens. */
const forgotPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/forgot-password',
  component: ForgotPasswordPage,
})

const resetPasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/reset-password',
  component: ResetPasswordPage,
})

/**
 * Enrol-during-login. Reached when a login returned `totpPending: true` —
 * the user holds a short-lived, scope-limited access token (no refresh
 * token) that only /v1/auth/totp/* and /v1/auth/me will accept. Lives
 * outside authedRoute for the same reason forceChangePasswordRoute does:
 * AppLayout's sidebar would tempt navigation into a 403 storm.
 */
const totpSetupRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/totp-setup',
  component: TotpSetupPage,
  beforeLoad: () => {
    const state = useAuthStore.getState()
    if (!hasLiveSession(state)) {
      if (state.accessToken || state.refreshToken) state.logout('session_expired')
      throw redirect({ to: '/login' })
    }
    // Already fully authenticated (e.g. a stale bookmark after finishing
    // enrolment) — nothing to set up here.
    if (!isTotpPending(state)) throw redirect({ to: '/' })
  },
})

/**
 * Forced password-change screen. Reached when login returned
 * passwordResetRequired=true; the user has an access token (so the route is
 * "authenticated") but the backend gate 403s everything except this surface.
 * Lives outside authedRoute so AppLayout's sidebar isn't shown — it would
 * tempt the user to navigate elsewhere and hit 403 storms.
 */
const forceChangePasswordRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/auth/change-password',
  component: ForceChangePasswordPage,
  beforeLoad: () => {
    const state = useAuthStore.getState()
    if (!hasLiveSession(state)) {
      if (state.accessToken || state.refreshToken) state.logout('session_expired')
      throw redirect({ to: '/login' })
    }
  },
})

/** Pathless layout route — wraps every authenticated screen with AppLayout. */
const authedRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: 'authed',
  component: AppLayout,
  beforeLoad: () => {
    // Admit the navigation only if the session is still viable — access token
    // live, OR a refresh token that can silently mint a new one. When only
    // the access token has aged out, we let the render proceed: the first
    // query's 401 triggers a silent refresh in the interceptor, so the user
    // sees a loading spinner, never the blank 401-storm screen. Only a fully
    // dead token pair redirects to /login.
    const state = useAuthStore.getState()
    if (!hasLiveSession(state)) {
      if (state.accessToken || state.refreshToken) state.logout('session_expired')
      throw redirect({ to: '/login' })
    }
    // totpPending sessions can only call /v1/auth/totp/* — admitting them
    // into the authed shell would just produce a 403 storm on every query.
    if (isTotpPending(state)) throw redirect({ to: '/auth/totp-setup' })
  },
})

const dashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/',
  component: DashboardPage,
})
const ingestionRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/ingest',
  component: IngestionPage,
})
const batchDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/ingest/$batchId',
  component: BatchDetailPage,
})
const casesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/cases',
  component: CasesPage,
})
const caseDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/cases/$caseId',
  component: CaseDetailPage,
})
const allocationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/allocations',
  component: AllocationsPage,
})
const collectionsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/collections',
  component: CollectionsPage,
})
const ptpReportRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/reports/ptp',
  component: PtpMisPage,
})
const visitReportRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/reports/visits',
  component: VisitMisPage,
})
const attendanceReportRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/reports/attendance',
  component: AttendanceMisPage,
})
// The Visit MIS's photo link, from the screen or a downloaded Excel cell.
const visitPhotoRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/reports/visit-photo/$visitId',
  component: VisitPhotoPage,
})
const settlementsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settlements',
  component: SettlementsPage,
})
const reconciliationRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/reconciliation',
  component: ReconciliationPage,
})
const settingsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/settings',
  component: SettingsPage,
})
const adminUsersRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/users',
  component: UsersAdminPage,
})
const adminOrgRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/org',
  component: OrgAdminPage,
})
const adminRolesRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/roles',
  component: RolesAdminPage,
})
const adminNotificationsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/notifications',
  component: NotificationsAdminPage,
})
const adminAuditRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/audit',
  component: AuditPage,
})
const adminPiiAccessRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/audit/pii-access',
  component: PiiAccessPage,
})
const cashConfirmRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/cash',
  component: CashConfirmPage,
})
const adminErasureRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/admin/erasure',
  component: ErasurePage,
})
const platformDashboardRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/platform/dashboard',
  component: PlatformDashboardPage,
})
const platformTenantsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/platform/tenants',
  component: TenantsPage,
})
const profileRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/profile',
  component: ProfilePage,
})
const profileSecurityRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/profile/security',
  component: SecurityPage,
})
const tripsApprovalsRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/trips/approvals',
  component: ApprovalCenterPage,
})
const tripsPlannerRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/trips/plan',
  component: PlannerPage,
})
const tripsReviewRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/trips/review',
  component: TripReviewPage,
})
const tripDetailRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/trips/$tripId',
  component: TripDetailPage,
})
const teleQueueRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/tele/queue',
  component: TeleQueuePage,
})
const teleConsoleRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/tele/console/$caseId',
  component: CallConsolePage,
})
const tripsLiveRoute = createRoute({
  getParentRoute: () => authedRoute,
  path: '/trips/live',
  component: LiveTripsPage,
})

const routeTree = rootRoute.addChildren([
  loginRoute,
  forgotPasswordRoute,
  resetPasswordRoute,
  forceChangePasswordRoute,
  totpSetupRoute,
  authedRoute.addChildren([
    dashboardRoute,
    ingestionRoute,
    batchDetailRoute,
    casesRoute,
    caseDetailRoute,
    allocationsRoute,
    collectionsRoute,
    ptpReportRoute,
    visitReportRoute,
    attendanceReportRoute,
    visitPhotoRoute,
    settlementsRoute,
    reconciliationRoute,
    settingsRoute,
    adminUsersRoute,
    adminOrgRoute,
    adminRolesRoute,
    adminNotificationsRoute,
    adminAuditRoute,
    adminPiiAccessRoute,
    cashConfirmRoute,
    adminErasureRoute,
    profileRoute,
    platformDashboardRoute,
    platformTenantsRoute,
    profileSecurityRoute,
    tripsApprovalsRoute,
    tripsPlannerRoute,
    tripsReviewRoute,
    // Register the static /trips/live BEFORE the dynamic
    // /trips/$tripId so it isn't matched as tripId='live'.
    tripsLiveRoute,
    tripDetailRoute,
    teleQueueRoute,
    teleConsoleRoute,
  ]),
])

const router = createRouter({
  routeTree,
  context: { queryClient },
  defaultPreload: 'intent',
})

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const rootEl = document.getElementById('root')
if (!rootEl) throw new Error('#root not found')

createRoot(rootEl).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  </StrictMode>,
)
